// Tariff code + name for a distribution config: a searchable list that is still
// a text field.
//
// Three things had to hold at once. The list is long — a distributor publishes
// hundreds of codes — so it needs a search box rather than a scroll. It must not
// be a closed vocabulary: a code the catalog has not been loaded with yet, or a
// one-off a distributor has not published, still has to be enterable by hand.
// And it renders inside `ContractDrawer`, which is a Radix `Sheet` — a modal
// dialog with a focus trap. A Popover/Command combobox portals its content
// outside that subtree and the trap then blocks it, so the list is rendered
// inline, under the input, while the field has focus.
//
// The input itself is the value: typing edits the code directly (that is the
// manual path), and choosing a suggestion fills in code and name together.

import { useState, useRef, useEffect } from 'react';
import { Check } from 'lucide-react';
import { Input } from '../../../../foundation/altzero_base/base/components/ui/input';
import { Label } from '../../../../foundation/altzero_base/base/components/ui/label';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import { filterTariffOptions, type TariffPickOption } from '../../utils/networkTariffs';

type Props = {
  code: string;
  name: string;
  options: TariffPickOption[];
  /** Both fields at once — picking a suggestion sets the name with the code. */
  onChange: (code: string, name: string) => void;
};

export function TariffCodePicker({ code, name, options, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on a click elsewhere in the drawer. `mousedown` rather than `click` so
  // the list is gone before the next field takes focus.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Search against what has been typed. An exact code shows the whole list again
  // rather than the single row that matches it — having selected EA225, opening
  // the field should let you get to EA256, not just confirm what is already set.
  const isExact = options.some((o) => o.tariff_code === code);
  const matches = filterTariffOptions(options, isExact ? '' : code);

  const pick = (option: TariffPickOption) => {
    onChange(option.tariff_code, option.tariff_name ?? '');
    setOpen(false);
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      <div ref={wrapRef} className="relative">
        <Label htmlFor="dc-tariff-code">Tariff Code</Label>
        <Input
          id="dc-tariff-code"
          autoComplete="off"
          className="mt-1 font-mono"
          placeholder="Search or type a code…"
          value={code}
          onFocus={() => setOpen(true)}
          onChange={(e) => { onChange(e.target.value, name); setOpen(true); }}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } }}
        />
        {open && (
          <div className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
            {matches.length === 0 ? (
              <p className="px-3 py-2.5 text-xs text-muted-foreground">
                {options.length === 0
                  ? 'No tariff codes defined yet — this code will be saved as typed.'
                  : 'No matching code — this one will be saved as typed.'}
              </p>
            ) : (
              matches.map((o) => (
                <button
                  key={o.tariff_code}
                  type="button"
                  onClick={() => pick(o)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-gray-50"
                >
                  <Check className={cn('h-3.5 w-3.5 shrink-0', o.tariff_code === code ? 'opacity-100' : 'opacity-0')} />
                  <span className="font-mono font-medium">{o.tariff_code}</span>
                  {o.tariff_name && <span className="truncate text-muted-foreground">{o.tariff_name}</span>}
                </button>
              ))
            )}
          </div>
        )}
      </div>
      <div>
        <Label htmlFor="dc-tariff-name">Tariff Name</Label>
        {/* Editable, not read-only: a hand-entered code has no catalog name to
            fill in, and leaving it locked would mean the row could never be
            labelled. Picking from the list overwrites whatever is here. */}
        <Input
          id="dc-tariff-name"
          className="mt-1"
          placeholder="Filled in when you pick a code"
          value={name}
          onChange={(e) => onChange(code, e.target.value)}
        />
      </div>
    </div>
  );
}
